"use client";

import { useState, useMemo, useEffect } from "react";
import { useParams } from "next/navigation";
import type { Params } from "next/dist/server/request/params";
import { type ColumnDef, type SortingState, type Cell_Core, useTable, tableFeatures, createPaginatedRowModel, rowPaginationFeature, columnFilteringFeature, createFilteredRowModel, rowSortingFeature, createSortedRowModel } from "@tanstack/react-table";
import { ArrowUpDownIcon, CircleX, SearchIcon } from "lucide-react";
import TablePagination from "@/components/table-pagination";
import Icon from "@/components/icon";
import cn from "@/utils/cn";
import { preserveHtmlSpaces, formatNumber } from "@/utils/format";
import { type PlayerStats, killfeed, leaderboard, getTrackedPlayerColorsById, getTrackedPlayerIds } from "..";
import useTableColumnFilters from "@/hooks/use-table-column-filters";
const features = tableFeatures({
  rowPaginationFeature,
  columnFilteringFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  sortedRowModel: createSortedRowModel()
});
const columns: ColumnDef<typeof features, PlayerStats>[] = [{
  id: "position",
  header: "#",
  enableSorting: false,
  meta: {
    alignRight: true,
    headerClassName: "w-15"
  }
},
// Main
{
  id: "p",
  accessorFn: row => preserveHtmlSpaces(killfeed.playerNames[row.id]),
  header: "Player",
  filterFn: (row, _, filterValue) => filterValue === null || killfeed.playerNames[row.original.id].toLowerCase().includes(filterValue.toLowerCase()),
  enableSorting: false,
  meta: {
    isPlayerName: true,
    className: "text-ellipsis overflow-x-hidden border-l"
  }
}, {
  id: "k",
  accessorFn: row => row.kills,
  header: "K",
  sortFn: (a, b) => a.original.kills - b.original.kills,
  sortDescFirst: true,
  meta: {
    alignRight: true,
    headerClassName: "w-20"
  }
}, {
  id: "d",
  accessorFn: row => row.deaths,
  header: "D",
  sortFn: (a, b) => a.original.deaths - b.original.deaths,
  sortDescFirst: true,
  meta: {
    alignRight: true,
    headerClassName: "w-20"
  }
}, {
  id: "kd",
  accessorFn: row => formatNumber(row.kills / Math.max(1, row.deaths), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }),
  header: "K/D",
  sortFn: (a, b) => a.original.kills / Math.max(a.original.deaths, 1) - b.original.kills / Math.max(b.original.deaths, 1),
  sortDescFirst: true,
  meta: {
    alignRight: true,
    headerClassName: "w-20",
    className: ""
  }
},
// Dominating weapon
{
  id: "dw",
  accessorFn: row => row.dominatingWeapon ? killfeed.weaponNames[row.dominatingWeapon.id] : "-",
  header: "Dominating Weapon",
  enableSorting: false,
  meta: {
    className: "text-ellipsis overflow-x-hidden border-l"
  }
}, {
  id: "dwk",
  accessorFn: row => row.dominatingWeapon ? row.dominatingWeapon.kills : "-",
  header: "K",
  enableSorting: false,
  meta: {
    alignRight: true,
    headerClassName: "w-20"
  }
},
// Nemesis weapon
{
  id: "nw",
  accessorFn: row => row.nemesisWeapon ? killfeed.weaponNames[row.nemesisWeapon.id] : "-",
  header: "Nemesis Weapon",
  enableSorting: false,
  meta: {
    className: "text-ellipsis overflow-x-hidden border-l"
  }
}, {
  id: "nwd",
  accessorFn: row => row.nemesisWeapon ? row.nemesisWeapon.deaths : "-",
  header: "D",
  enableSorting: false,
  meta: {
    alignRight: true,
    headerClassName: "w-20",
    className: "text-ellipsis overflow-x-hidden"
  }
},
// Dominating player
{
  id: "dp",
  accessorFn: row => row.dominatingPlayer ? preserveHtmlSpaces(killfeed.playerNames[row.dominatingPlayer.id]) : "-",
  header: "Dominating Player",
  enableSorting: false,
  meta: {
    isPlayerName: true,
    className: "text-ellipsis overflow-x-hidden border-l"
  }
}, {
  id: "dpk",
  accessorFn: row => row.dominatingPlayer ? row.dominatingPlayer.kills : "-",
  header: "K",
  enableSorting: false,
  meta: {
    alignRight: true,
    headerClassName: "w-20"
  }
}, {
  id: "dpd",
  accessorFn: row => row.dominatingPlayer ? row.dominatingPlayer.deaths : "-",
  header: "D",
  enableSorting: false,
  meta: {
    alignRight: true,
    headerClassName: "w-20"
  }
}, {
  id: "dpkd",
  accessorFn: row => row.dominatingPlayer ? formatNumber(row.dominatingPlayer.kills / Math.max(row.dominatingPlayer.deaths, 1), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }) : 0,
  header: "K/D",
  enableSorting: false,
  meta: {
    alignRight: true,
    headerClassName: "w-20"
  }
},
// Nemesis player
{
  id: "np",
  accessorFn: row => row.nemesisPlayer ? preserveHtmlSpaces(killfeed.playerNames[row.nemesisPlayer.id]) : "-",
  header: "Nemesis Player",
  enableSorting: false,
  meta: {
    isPlayerName: true,
    className: "text-ellipsis overflow-x-hidden border-l"
  }
}, {
  id: "npd",
  accessorFn: row => row.nemesisPlayer ? row.nemesisPlayer.deaths : "-",
  header: "D",
  enableSorting: false,
  meta: {
    alignRight: true,
    headerClassName: "w-20"
  }
}, {
  id: "npk",
  accessorFn: row => row.nemesisPlayer ? row.nemesisPlayer.kills : 0,
  header: "K",
  enableSorting: false,
  meta: {
    alignRight: true,
    headerClassName: "w-20"
  }
}, {
  id: "npdk",
  accessorFn: row => row.nemesisPlayer ? formatNumber(row.nemesisPlayer.deaths / Math.max(row.nemesisPlayer.kills, 1), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }) : 0,
  header: "D/K",
  enableSorting: false,
  meta: {
    alignRight: true,
    headerClassName: "w-20",
    className: "pr-5"
  }
}];
export default function LeaderboardTable({
  params
}: {
  params: Params;
}) {
  params = useParams() ?? params;
  const wipeIndex = Number(params.wipeId) - 1;
  const trackedPlayerIds = getTrackedPlayerIds(wipeIndex);
  const trackedPlayerColorsById = getTrackedPlayerColorsById(trackedPlayerIds, wipeIndex);
  const [data, setData] = useState(() => leaderboard[wipeIndex]);
  const [trackedPlayerData, setTrackedPlayerData] = useState(() => leaderboard[wipeIndex].filter(stats => trackedPlayerIds.includes(stats.id)));
  const {
    columnFilters,
    setColumnFilters,
    getColumnFilter,
    setColumnFilter
  } = useTableColumnFilters([]);
  const [sorting, setSorting] = useState<SortingState>([{
    id: "k",
    desc: true
  }]);
  const table = useTable({
    features,
    columns,
    data,
    initialState: {
      pagination: {
        pageSize: 100,
        pageIndex: 0
      }
    },
    state: {
      columnFilters,
      sorting
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    enableSortingRemoval: false
  });
  const trackedPlayersTable = useTable({
    // @ts-ignore
    columns,
    data: trackedPlayerData,
    enableSortingRemoval: false
  });
  useEffect(() => {
    if (!params?.wipeId) return;
    setData(leaderboard[wipeIndex]);
    setTrackedPlayerData(leaderboard[wipeIndex].filter(stats_0 => trackedPlayerIds.includes(stats_0.id)));
  }, [wipeIndex]);
  const name = getColumnFilter("p") as string;
  const setName = (name_0?: string) => setColumnFilter("p", name_0 || undefined);
  return <>
            <div className="h-8 pr-2.5 flex w-full overflow-x-auto whitespace-nowrap">
                <div className="relative flex items-center border-r px-2.5">
                    <Icon component={SearchIcon} className="absolute inset-s-2.5 flex items-center pointer-events-none text-neutral-400" />
                    <input type="text" name="filter" placeholder="Type to filter..." value={name ?? ""} onChange={(e: any) => setName(e.target.value)} className="px-6 outline-none" />
                    {!!name && <button onClick={() => setName()} className="absolute inset-e-2.5 flex items-center text-neutral-400 cursor-pointer hover:text-neutral-100">
                            <Icon component={CircleX} />
                        </button>}
                </div>
            </div>

            <div className="overflow-x-auto max-w-full 2xl:overflow-x-visible 2xl:max-w-none">
                <table className="w-full relative bg-neutral-950 2xl:table-fixed">
                    <thead className="2xl:sticky 2xl:top-9.75 bg-neutral-900">
                        {table.getHeaderGroups().map(headerGroup => <tr key={headerGroup.id} className="bg-neutral-900">
                                {headerGroup.headers.map(header => <th key={header.id} className={cn("whitespace-nowrap", (header.column.columnDef?.meta as any)?.headerClassName)}>
                                        <div className={cn("h-10 flex items-center px-2.5 border-t border-b-3", (header.column.columnDef?.meta as any)?.alignRight && "justify-end", (header.column.columnDef?.meta as any)?.className)}>
                                            {header.isPlaceholder ? null : header.column.getCanSort() ? <button onClick={header.column.getToggleSortingHandler()} className={cn("flex items-center gap-1.5 rounded cursor-pointer px-2 py-1 translate-x-2", header.column.getIsSorted() ? "bg-blue-500 hover:bg-blue-400" : "hover:bg-neutral-950")}>
                                                        <table.FlexRender header={header} />
                                                        <Icon component={ArrowUpDownIcon} />
                                                    </button> : <table.FlexRender header={header} />}
                                        </div>
                                    </th>)}
                            </tr>)}
                    </thead>

                    <tbody>
                        {!table.state.columnFilters.length && trackedPlayersTable.getRowModel().rows.map((row, rowIndex) => <tr key={row.id} className={cn("border-b hover:bg-neutral-900/50", rowIndex === trackedPlayersTable.getRowModel().rows.length - 1 && "border-b-3")}>
                                {row.getAllCells().map((cell, i) => <td key={cell.id} className={cn("whitespace-nowrap px-2.5 py-1.5", (cell.column.columnDef?.meta as any)?.alignRight && "text-right", (cell.column.columnDef?.meta as any)?.className, (cell.column.columnDef?.meta as any)?.isPlayerName && getTrackedPlayerColor(cell, trackedPlayerColorsById))}>
                                        {i
              // @ts-ignore
              ? <table.FlexRender cell={cell} /> : table.getSortedRowModel().rows.findIndex(sortedRow => sortedRow.original.id === (row.original as any).id) + 1}
                                    </td>)}
                            </tr>)}
                        
                        {table.getRowModel().rows.map((row_0, rowIndex_0) => <tr key={row_0.id} className={cn("border-b hover:bg-neutral-900/50")}>
                                {row_0.getAllCells().map((cell_0, i_0) => <td key={cell_0.id} className={cn("whitespace-nowrap px-2.5 py-1.5", (cell_0.column.columnDef?.meta as any)?.alignRight && "text-right", (cell_0.column.columnDef?.meta as any)?.className, (cell_0.column.columnDef?.meta as any)?.isPlayerName && getTrackedPlayerColor(cell_0, trackedPlayerColorsById))}>
                                        {i_0 ? <table.FlexRender cell={cell_0} /> : table.state.pagination.pageIndex * table.state.pagination.pageSize + rowIndex_0 + 1}
                                    </td>)}
                            </tr>)}
                    </tbody>
                </table>
            </div>
            
            <TablePagination variant="glued" page={table.state.pagination.pageIndex + 1} pageCount={table.getPageCount()} setPage={(page: number) => table.setPageIndex(page - 1)} />
        </>;
}
export function getTrackedPlayerColor(cell: Cell_Core<any, any>, trackedPlayerColorsById: Record<number, string>) {
  switch (cell.column.id) {
    case "p":
      return cell.row.original.id in trackedPlayerColorsById && trackedPlayerColorsById[cell.row.original.id];
    case "dp":
      return cell.row.original.dominatingPlayer && cell.row.original.dominatingPlayer.id in trackedPlayerColorsById && trackedPlayerColorsById[cell.row.original.dominatingPlayer.id];
    case "np":
      return cell.row.original.nemesisPlayer && cell.row.original.nemesisPlayer.id in trackedPlayerColorsById && trackedPlayerColorsById[cell.row.original.nemesisPlayer.id];
  }
}